import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Chart, registerables } from 'chart.js';
import { MeterAnalyticsPeriod, MeterAnalyticsQuery, MeterSnapshotResponse, MeterDistributionResponse, MeterTrendsResponse, MeterGeoResponse } from '../../models/meter-analytics.model';
import { MeterAnalyticsService } from '../../services/meter-analytics.service';

Chart.register(...registerables);

@Component({
    selector: 'app-meter-analytics',
    templateUrl: './meter-analytics.component.html',
    styleUrls: ['./meter-analytics.component.scss'],
})
export class MeterAnalyticsComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    selectedPeriod: MeterAnalyticsPeriod = '30d';
    comparePrevious = false;
    customFrom: string | null = null;
    customTo: string | null = null;
    showCustomDates = false;

    loading = false;
    error = false;

    snapshot: MeterSnapshotResponse | null = null;
    distribution: MeterDistributionResponse | null = null;
    trends: MeterTrendsResponse | null = null;
    geo: MeterGeoResponse | null = null;

    selectedNodeTypes: string[] = [];
    selectedAlertTypes: string[] = [];

    nodeTypeOptions: string[] = ['WATER_METER'];
    alertTypeOptions: string[] = ['Leak', 'Continuous Flow', 'High Flow'];

    periods: { value: MeterAnalyticsPeriod; labelKey: string }[] = [
        { value: '7d', labelKey: 'meter.analytics.period_7d' },
        { value: '30d', labelKey: 'meter.analytics.period_30d' },
        { value: '90d', labelKey: 'meter.analytics.period_90d' },
        { value: 'custom', labelKey: 'meter.analytics.period_custom' },
    ];

    constructor(
        private analyticsService: MeterAnalyticsService,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.loadData();

        this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.loadData();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onPeriodChange(period: MeterAnalyticsPeriod): void {
        this.selectedPeriod = period;
        this.showCustomDates = period === 'custom';

        if (period !== 'custom') {
            this.customFrom = null;
            this.customTo = null;
            this.loadData();
        }
    }

    onCompareToggle(): void {
        this.comparePrevious = !this.comparePrevious;
        this.loadData();
    }

    onCustomDateChange(): void {
        if (this.customFrom && this.customTo) {
            this.loadData();
        }
    }

    onNodeTypeChange(types: string[]): void {
        this.selectedNodeTypes = types;
        this.loadData();
    }

    onAlertTypeChange(types: string[]): void {
        this.selectedAlertTypes = types;
        this.loadData();
    }

    retry(): void {
        this.loadData();
    }

    loadData(): void {
        if (this.selectedPeriod === 'custom' && (!this.customFrom || !this.customTo)) {
            return;
        }

        const query: MeterAnalyticsQuery = {
            period: this.selectedPeriod,
            comparePrevious: this.comparePrevious,
        };

        if (this.selectedPeriod === 'custom') {
            query.from = this.customFrom;
            query.to = this.customTo;
        }

        if (this.selectedNodeTypes.length > 0) {
            query.nodeType = this.selectedNodeTypes;
        }

        if (this.selectedAlertTypes.length > 0) {
            query.alertType = this.selectedAlertTypes;
        }

        this.loading = true;
        this.error = false;

        forkJoin({
            snapshot: this.analyticsService.getSnapshot(query),
            distribution: this.analyticsService.getDistribution(query),
            trends: this.analyticsService.getTrends(query),
            geo: this.analyticsService.getGeo(query),
        })
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.loading = false;
                }),
            )
            .subscribe({
                next: (result) => {
                    this.snapshot = result.snapshot;
                    this.distribution = result.distribution;
                    this.trends = result.trends;
                    this.geo = result.geo;
                },
                error: () => {
                    this.error = true;
                    this.snapshot = null;
                    this.distribution = null;
                    this.trends = null;
                    this.geo = null;
                },
            });
    }
}
